import { Link, useLocation } from "@tanstack/react-router";
import { Cookie } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/field";
import { Sheet } from "@/components/ui/sheet";
import { t } from "@/i18n";
import { useConsent } from "@/lib/consent";
import { isTokenPage } from "@/lib/utils";

/**
 * Non-blocking notice at the bottom of the viewport until the visitor decides,
 * plus the settings sheet that can be reopened from any "Cookie settings" link.
 *
 * Design constraints from Datatilsynet's 2025 guidance: no cookie wall, no
 * pre-ticked boxes, declining is one click and as visible as accepting, and
 * the choice can be changed as easily as it was made.
 */
export function CookieBanner() {
  const c = useConsent();
  const { pathname } = useLocation();
  // Public token pages set nothing and store nothing on the device, so there is nothing to ask about (ekomloven § 3-15).
  if (isTokenPage(pathname)) return null;
  return (
    <>
      {c.record === null && !c.settingsOpen && <Banner />}
      <CookieSettingsSheet />
    </>
  );
}

function Banner() {
  const { analyticsAvailable, decide, openSettings } = useConsent();
  return (
    <section role="region" aria-label={t("legal.cookieNotice")} className="print-hidden fixed inset-x-0 bottom-0 z-40 p-3 sm:p-4 safe-bottom">
      <div className="card mx-auto flex max-w-2xl flex-col gap-3 p-4 shadow-2xl sm:flex-row sm:items-center sm:gap-4">
        <div className="flex flex-1 items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/12 text-accent"><Cookie className="h-5 w-5" aria-hidden /></span>
          <p className="text-sm text-text-secondary">
            {analyticsAvailable ? t("legal.bannerWithAnalytics") : t("legal.bannerNecessaryOnly")}{" "}
            <Link to="/legal/cookies" className="font-semibold text-accent">{t("legal.cookiePolicy")}</Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 sm:shrink-0">
          {analyticsAvailable ? (
            <>
              <Button variant="ghost" size="sm" onClick={openSettings}>{t("legal.cookieSettings")}</Button>
              {/* Same variant, same size: declining is exactly as easy as accepting. */}
              <Button variant="secondary" size="sm" onClick={() => decide(false)}>{t("legal.necessaryOnly")}</Button>
              <Button variant="secondary" size="sm" onClick={() => decide(true)}>{t("legal.acceptAll")}</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={openSettings}>{t("legal.details")}</Button>
              <Button variant="secondary" size="sm" onClick={() => decide(false)}>{t("legal.ok")}</Button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function CookieSettingsSheet() {
  const { record, analyticsAvailable, settingsOpen, closeSettings, decide } = useConsent();
  const [analytics, setAnalytics] = useState(record?.analytics ?? false);
  // Re-sync the toggle each time the sheet opens (the record may have changed meanwhile).
  useEffect(() => { if (settingsOpen) setAnalytics(record?.analytics ?? false); }, [settingsOpen, record]);

  const save = () => { decide(analytics); closeSettings(); };

  return (
    <Sheet
      open={settingsOpen}
      onClose={closeSettings}
      title={t("legal.cookieSettings")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={closeSettings}>{t("common.cancel")}</Button>
          <Button onClick={save}>{t("legal.save")}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-control bg-bg p-3">
          <Switch checked disabled onChange={() => undefined} label={t("legal.necessary")} hint={t("legal.necessaryHint")} />
        </div>
        <div className="rounded-control bg-bg p-3">
          <Switch checked={analyticsAvailable && analytics} disabled={!analyticsAvailable} onChange={setAnalytics} label={t("legal.analytics")} hint={analyticsAvailable ? t("legal.analyticsHint") : t("legal.analyticsUnavailable")} />
        </div>
        <p className="text-xs text-text-tertiary">
          <Link to="/legal/cookies" onClick={closeSettings} className="font-semibold text-accent">{t("legal.cookiePolicy")}</Link>
          {" · "}
          <Link to="/legal/privacy" onClick={closeSettings} className="font-semibold text-accent">{t("legal.privacy")}</Link>
        </p>
      </div>
    </Sheet>
  );
}
